import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Ditado no campo de mensagem — porte do `useBrainVoiceComposer` do Brain
 * original: grava em pedaços, mostra o texto ao vivo, pausa, retoma, salva
 * (transcrição final do áudio inteiro) e cancela (volta o texto de antes).
 *
 * O texto vai para o campo, não para o Brain: a pessoa confere antes de enviar.
 * Cada transcrição passa por `brain-voz/transcrever` e conta no teto de IA da
 * clínica; por isso a transcrição ao vivo roda a cada 4 s, e não a cada pedaço
 * de 2 s como no original.
 */

const PEDACO_MS = 2000;
/** Tempo máximo esperando o último pedaço de áudio depois de parar a gravação. */
const ESPERA_DO_ULTIMO_MS = 1500;
const INTERVALO_AO_VIVO_MS = 4000;
const MENOR_AUDIO_BYTES = 1200;

export function formatarDuracao(segundos: number): string {
    const min = Math.floor(segundos / 60);
    const seg = segundos % 60;
    return `${String(min).padStart(2, '0')}:${String(seg).padStart(2, '0')}`;
}

function melhorFormato(): string {
    if (typeof MediaRecorder === 'undefined') return '';
    return ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/wav'].find((t) => MediaRecorder.isTypeSupported(t)) ?? '';
}

/** Mensagem que a pessoa entende quando o navegador recusa o microfone. */
function erroDoMicrofone(e: unknown): Error {
    const nome = typeof e === 'object' && e !== null && 'name' in e ? String((e as { name: unknown }).name) : '';
    let mensagem = e instanceof Error && e.message ? e.message : 'Não consegui ligar o microfone.';
    if (nome === 'NotAllowedError' || nome === 'SecurityError') {
        mensagem = 'Permita o acesso ao microfone nas configurações do navegador para ditar.';
    } else if (nome === 'NotFoundError' || nome === 'OverconstrainedError') {
        mensagem = 'Nenhum microfone encontrado neste aparelho.';
    } else if (nome === 'NotReadableError' || nome === 'AbortError') {
        mensagem = 'O microfone está sendo usado por outro app.';
    }
    const embrulhado = new Error(mensagem);
    embrulhado.name = nome || 'Error';
    return embrulhado;
}

export interface Ditado {
    gravando: boolean;
    salvando: boolean;
    pausado: boolean;
    duracao: number;
    suportado: boolean;
    iniciar: (textoAtual: string) => Promise<void>;
    /** Para, transcreve o áudio inteiro e devolve a mensagem final. */
    salvar: () => Promise<string>;
    /** Joga a gravação fora e devolve o texto que havia antes. */
    cancelar: () => void;
    pausar: () => void;
    retomar: () => void;
}

export interface OpcoesDoDitado {
    aoMudarTexto: (texto: string) => void;
    transcrever: (audio: Blob) => Promise<string>;
}

export function useDitado({ aoMudarTexto, transcrever }: OpcoesDoDitado): Ditado {
    const [gravando, setGravando] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [pausado, setPausado] = useState(false);
    const [duracao, setDuracao] = useState(0);

    const base = useRef('');
    const falado = useRef('');
    const relogio = useRef<ReturnType<typeof setInterval> | null>(null);
    const fluxo = useRef<MediaStream | null>(null);
    const gravador = useRef<MediaRecorder | null>(null);
    const pedacos = useRef<Blob[]>([]);
    const processando = useRef(false);
    const ultimaAoVivo = useRef(0);
    const ultimoTexto = useRef('');
    const falhas = useRef(0);
    const ativo = useRef(false);
    const salvandoRef = useRef(false);
    const pausadoRef = useRef(false);
    const mudar = useRef(aoMudarTexto);
    const transcreverRef = useRef(transcrever);
    const salvarRef = useRef<(() => Promise<string>) | null>(null);
    mudar.current = aoMudarTexto;
    transcreverRef.current = transcrever;

    const suportado = typeof MediaRecorder !== 'undefined'
        && typeof navigator !== 'undefined'
        && typeof navigator.mediaDevices?.getUserMedia === 'function';

    const pararRelogio = useCallback(() => {
        if (relogio.current) clearInterval(relogio.current);
        relogio.current = null;
    }, []);

    const iniciarRelogio = useCallback(() => {
        pararRelogio();
        relogio.current = setInterval(() => setDuracao((d) => d + 1), 1000);
    }, [pararRelogio]);

    const textoDoCampo = useCallback(() => {
        const b = base.current.trim();
        const f = falado.current.trim();
        if (!b) return f;
        if (!f) return b;
        return `${b} ${f}`;
    }, []);

    const soltarMicrofone = useCallback(() => {
        fluxo.current?.getTracks().forEach((trilha) => {
            trilha.onended = null;
            trilha.stop();
        });
        fluxo.current = null;
    }, []);

    const pararGravador = useCallback(() => {
        const g = gravador.current;
        gravador.current = null;
        if (!g) return;
        try {
            g.ondataavailable = null;
            if (g.state !== 'inactive') g.stop();
        } catch {
            /* já parado */
        }
    }, []);

    /** Para e espera o último pedaço chegar: sem isso, o fim da frase ficava de fora. */
    const esvaziarGravador = useCallback(() => {
        const g = gravador.current;
        gravador.current = null;
        if (!g || g.state === 'inactive') return Promise.resolve();
        return new Promise<void>((resolve) => {
            let feito = false;
            const terminar = () => {
                if (feito) return;
                feito = true;
                g.ondataavailable = null;
                resolve();
            };
            g.addEventListener('stop', terminar, { once: true });
            setTimeout(terminar, ESPERA_DO_ULTIMO_MS);
            try {
                if (g.state === 'paused') g.resume();
                g.stop();
            } catch {
                terminar();
            }
        });
    }, []);

    const transcreverAoVivo = useCallback(async () => {
        if (processando.current || !ativo.current || pausadoRef.current || salvandoRef.current) return;
        if (Date.now() - ultimaAoVivo.current < INTERVALO_AO_VIVO_MS || falhas.current >= 3) return;
        const junto = new Blob(pedacos.current, { type: pedacos.current[0]?.type ?? 'audio/webm' });
        if (junto.size < MENOR_AUDIO_BYTES) return;
        processando.current = true;
        ultimaAoVivo.current = Date.now();
        try {
            const texto = (await transcreverRef.current(junto)).trim();
            if (!ativo.current || salvandoRef.current) return;
            if (texto && texto !== ultimoTexto.current) {
                ultimoTexto.current = texto;
                falhas.current = 0;
                falado.current = texto;
                mudar.current(textoDoCampo());
            }
        } catch {
            falhas.current += 1;
        } finally {
            processando.current = false;
        }
    }, [textoDoCampo]);

    const iniciar = useCallback(async (textoAtual: string) => {
        if (!suportado) throw new Error('Ditado por voz não disponível neste navegador.');
        base.current = textoAtual.trim();
        falado.current = '';
        pedacos.current = [];
        processando.current = false;
        ultimaAoVivo.current = Date.now();
        ultimoTexto.current = '';
        falhas.current = 0;
        ativo.current = true;
        salvandoRef.current = false;
        pausadoRef.current = false;
        mudar.current(base.current);
        setGravando(true);
        setPausado(false);
        setDuracao(0);

        try {
            const microfone = await navigator.mediaDevices.getUserMedia({
                audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
            });
            fluxo.current = microfone;
            /* Ligação, outro app ou tela bloqueada encerram o microfone: salva o que já foi dito. */
            microfone.getAudioTracks().forEach((trilha) => {
                trilha.onended = () => {
                    if (ativo.current && !salvandoRef.current) void salvarRef.current?.();
                };
            });
            const formato = melhorFormato();
            const novo = formato ? new MediaRecorder(microfone, { mimeType: formato }) : new MediaRecorder(microfone);
            gravador.current = novo;
            novo.ondataavailable = (evento: BlobEvent) => {
                /* Todo pedaço entra, inclusive o silêncio: pular um deixa buraco no arquivo. */
                if (!evento.data || evento.data.size === 0) return;
                if (!ativo.current && !salvandoRef.current) return;
                pedacos.current.push(evento.data);
                void transcreverAoVivo();
            };
            novo.onerror = () => {
                if (ativo.current && !salvandoRef.current) void salvarRef.current?.();
            };
            novo.start(PEDACO_MS);
        } catch (e) {
            /* Permissão negada ou microfone ocupado: a barra "Gravando" não fica presa. */
            ativo.current = false;
            pararGravador();
            soltarMicrofone();
            setGravando(false);
            setDuracao(0);
            mudar.current(base.current);
            throw erroDoMicrofone(e);
        }
        iniciarRelogio();
    }, [iniciarRelogio, pararGravador, soltarMicrofone, suportado, transcreverAoVivo]);

    const cancelar = useCallback(() => {
        ativo.current = false;
        salvandoRef.current = false;
        pausadoRef.current = false;
        pararGravador();
        soltarMicrofone();
        pararRelogio();
        mudar.current(base.current);
        base.current = '';
        falado.current = '';
        pedacos.current = [];
        setGravando(false);
        setPausado(false);
        setDuracao(0);
        setSalvando(false);
    }, [pararGravador, pararRelogio, soltarMicrofone]);

    const pausar = useCallback(() => {
        if (!ativo.current || pausadoRef.current || salvandoRef.current) return;
        pausadoRef.current = true;
        setPausado(true);
        if (gravador.current?.state === 'recording') gravador.current.pause();
        pararRelogio();
    }, [pararRelogio]);

    const retomar = useCallback(() => {
        if (!ativo.current || !pausadoRef.current || salvandoRef.current) return;
        pausadoRef.current = false;
        setPausado(false);
        if (gravador.current?.state === 'paused') gravador.current.resume();
        iniciarRelogio();
    }, [iniciarRelogio]);

    const salvar = useCallback(async () => {
        if (!ativo.current) return '';
        salvandoRef.current = true;
        ativo.current = false;
        pausadoRef.current = false;
        setSalvando(true);
        pararRelogio();
        await esvaziarGravador();
        soltarMicrofone();

        let dito = falado.current.trim();
        if (pedacos.current.length > 0) {
            const junto = new Blob(pedacos.current, { type: pedacos.current[0]?.type ?? 'audio/webm' });
            try {
                /* A transcrição do áudio inteiro garante a frase completa. */
                const final = (await transcreverRef.current(junto)).trim();
                if (final) dito = final;
            } catch {
                /* Fica o melhor texto ao vivo que já havia. */
            }
        }
        const b = base.current.trim();
        const mensagem = [b, dito].filter(Boolean).join(' ');
        mudar.current(mensagem);
        base.current = '';
        falado.current = '';
        pedacos.current = [];
        setGravando(false);
        setPausado(false);
        setSalvando(false);
        salvandoRef.current = false;
        setDuracao(0);
        return mensagem;
    }, [esvaziarGravador, pararRelogio, soltarMicrofone]);
    salvarRef.current = salvar;

    useEffect(() => () => {
        ativo.current = false;
        salvandoRef.current = false;
        pausadoRef.current = false;
        pararGravador();
        soltarMicrofone();
        pararRelogio();
    }, [pararGravador, pararRelogio, soltarMicrofone]);

    return { gravando, salvando, pausado, duracao, suportado, iniciar, salvar, cancelar, pausar, retomar };
}
